import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, faqs } from '@/mocks/articles';

export default function About() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: '感谢您的投稿！我们会在1-2个工作日内进行审核。' });
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qs1vsnlsngrm4lm50g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的投稿！我们会在1-2个工作日内进行审核。' });
        form.reset();
      } else if (!response.ok || serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后重试。' });
      } else {
        setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后重试。' });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络连接后重试。' });
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heilao-buda.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'Organization',
        name: '黑料不打',
        url: 'https://heilao-buda.com',
        description: '专注收录真实用户评价与行业口碑的聚合平台',
        email: 'contact@heilao-buda.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '北京',
          addressCountry: 'CN',
        },
        foundingDate: '2024',
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 黑料不打',
        description: '了解黑料不打平台的品牌故事、使命愿景、团队介绍和联系方式',
        url: 'https://heilao-buda.com/about',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50 border-b border-background-200/60">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
              关于黑料不打
            </h1>

            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  在信息爆炸的时代，消费者面临着前所未有的选择困难。虚假宣传、水军刷评、恶意抹黑等乱象层出不穷，让真正的消费口碑变得难以分辨。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                  <strong>黑料不打</strong>应运而生。我们致力于打造一个可信赖的真实口碑聚合平台，通过严格的信息审核机制，筛选和呈现经过核实的消费评价与行业调查，帮助每一位消费者做出明智的决策。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                  我们的名字"黑料不打"代表的不是鼓励恶意攻击，而是——
                  <span className="text-primary-600 font-semibold">真实的"黑料"我们不打压，虚假的"爆料"我们不放过</span>。
                  我们坚持客观、公正、透明的原则，拒绝成为任何利益方的工具。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 bg-background-100 rounded-lg text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mt-3">严格核实</h3>
                  <p className="text-xs text-foreground-500 mt-1">每一条信息都经过AI+人工双重验证</p>
                </div>
                <div className="p-5 bg-background-100 rounded-lg text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-scales-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mt-3">客观公正</h3>
                  <p className="text-xs text-foreground-500 mt-1">拒绝利益捆绑，只对事实负责</p>
                </div>
                <div className="p-5 bg-background-100 rounded-lg text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-eye-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mt-3">信息透明</h3>
                  <p className="text-xs text-foreground-500 mt-1">明确标注核实状态，公开审核流程</p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  媒体口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      quote: '黑料不打以其严谨的调查方法和客观的报道立场，成为消费者维权的重要参考平台。',
                      source: '消费者报道',
                    },
                    {
                      quote: '在消费信息鱼龙混杂的今天，黑料不打提供了一个难得的可信信息源。其双重审核机制值得行业借鉴。',
                      source: '财经观察',
                    },
                    {
                      quote: '黑料不打的每一篇报道都经过扎实的调查，为消费者提供了宝贵的避坑指南。',
                      source: '生活周刊',
                    },
                    {
                      quote: '作为消费者权益保护的践行者，黑料不打正在重塑消费信息的传播方式。',
                      source: '经济日报',
                    },
                  ].map((item, idx) => (
                    <div key={idx} className="p-5 bg-background-100 rounded-lg">
                      <p className="text-sm text-foreground-700 leading-relaxed italic">
                        "{item.quote}"
                      </p>
                      <p className="text-xs text-foreground-500 mt-3 font-medium">—— {item.source}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  平台数据
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">{articles.length}+</p>
                    <p className="text-xs text-foreground-500 mt-1">深度调查报道</p>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">12</p>
                    <p className="text-xs text-foreground-500 mt-1">覆盖行业领域</p>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">1000万+</p>
                    <p className="text-xs text-foreground-500 mt-1">月度读者</p>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg">
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">99.7%</p>
                    <p className="text-xs text-foreground-500 mt-1">信息准确率</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  投稿爆料
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  如果您遭遇了消费陷阱，或发现了某品牌的不当行为，欢迎向我们投稿。请提供尽可能详细的信息，包括：
                </p>
                <ul className="space-y-2 mb-6">
                  {['消费时间、地点和金额', '详细的问题描述', '相关的证据材料（消费凭证、照片、聊天记录等）', '您的联系方式（用于后续核实，不会公开）'].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4 p-6 bg-background-100 rounded-lg"
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">投稿表单</h3>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="brand" className="block text-sm font-medium text-foreground-700 mb-1">
                      被评价品牌/公司
                    </label>
                    <input
                      type="text"
                      id="brand"
                      name="brand"
                      className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您要评价的品牌名称"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      详细描述 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请详细描述您的消费经历（限500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-form-alt"
                  />

                  <button
                    type="submit"
                    className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    提交投稿
                  </button>

                  {formStatus && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        formStatus.type === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer">
                      <summary className="flex items-center justify-between p-4 text-sm font-medium text-foreground-900 cursor-pointer">
                        {faq.question}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@heilao-buda.com</p>
                      <p className="text-xs text-foreground-400 mt-1">新闻报道与商务合作</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">微信公众号</p>
                      <p className="text-sm text-foreground-600">黑料不打</p>
                      <p className="text-xs text-foreground-400 mt-1">搜索关注，获取最新报道</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">办公地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区建国路88号</p>
                      <p className="text-xs text-foreground-400 mt-1">SOHO现代城A座15层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">联系电话</p>
                      <p className="text-sm text-foreground-600">010-8888-6666</p>
                      <p className="text-xs text-foreground-400 mt-1">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}